import './feature-tabs.css';

export default () => {
  class featureTabs extends HTMLElement {
    constructor() {
      super();
      this.initializeTabs = this.initializeTabs.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleContentKeydown = this.handleContentKeydown.bind(this);
      this.currentTabIndex = 0;
      this.isNavigatingTabs = false;
    }

    connectedCallback() {
      this.initializeTabs();
      window.addEventListener('resize', this.initializeTabs);
      // Add global keydown listener for better arrow key handling
      document.addEventListener('keydown', this.handleContentKeydown);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.initializeTabs);
      document.removeEventListener('keydown', this.handleContentKeydown);
      this.columns.forEach(column => {
        column.removeEventListener('keydown', this.handleKeydown);
      });
    }

    // Enhanced content keydown handler for better arrow navigation
    handleContentKeydown(event) {
      // Only handle if focus is within this component
      if (!this.contains(document.activeElement)) {
        return;
      }

      const { key, target, ctrlKey, metaKey } = event;

      // Handle Ctrl/Cmd + Arrow for quick tab navigation from anywhere in component
      if ((ctrlKey || metaKey) && (key === 'ArrowLeft' || key === 'ArrowRight')) {
        event.preventDefault();

        let newIndex = this.currentTabIndex;
        if (key === 'ArrowRight') {
          newIndex = (this.currentTabIndex + 1) % this.columns.length;
        } else {
          newIndex = (this.currentTabIndex - 1 + this.columns.length) % this.columns.length;
        }

        this.activateColumn(this.columns[newIndex], true);
        return;
      }

      // Enhanced arrow navigation when focus is on tab content
      const activeContent = this.querySelector('.js-section--animation.show');
      if (activeContent && activeContent.contains(target)) {
        // Allow content-specific navigation
        if (key === 'Escape') {
          event.preventDefault();
          // Return focus to active tab
          this.columns[this.currentTabIndex].focus();
          return;
        }

        // Alt + Arrow for tab navigation from content
        if (event.altKey && (key === 'ArrowLeft' || key === 'ArrowRight')) {
          event.preventDefault();

          let newIndex = this.currentTabIndex;
          if (key === 'ArrowRight') {
            newIndex = (this.currentTabIndex + 1) % this.columns.length;
          } else {
            newIndex = (this.currentTabIndex - 1 + this.columns.length) % this.columns.length;
          }

          this.activateColumn(this.columns[newIndex], false);
        }
      }
    }

    activateColumn(column, focus = false) {
      const columnClassAnimation = column.querySelector('.js-section--animation');

      this.contents.forEach(cont => {
        cont.classList.add("hide");

        if (!column.classList.contains('active')) {
          cont.classList.remove("show", "fade");
        }
      });

      this.columns.forEach((item, index) => {
        item.classList.remove("active");
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('tabindex', '-1');

        if (item === column) {
          this.currentTabIndex = index;
        }
      });

      column.classList.add("active");
      column.setAttribute('aria-selected', 'true');
      column.setAttribute('tabindex', '0');

      if (focus) {
        column.focus();
      }

      // Announce tab change to screen readers
      this.announceTabChange(column);

      columnClassAnimation.classList.remove("hide");
      columnClassAnimation.classList.add("show");

      if (!columnClassAnimation.classList.contains('fade')) {
        setTimeout(() => {
          columnClassAnimation.classList.add("fade");
        }, 100);
      }
    }

    // Announce tab changes for better accessibility
    announceTabChange(column) {
      const tabTitle = column.querySelector('.section-content__meta-title');
      if (tabTitle) {
        // Create or update live region for screen reader announcements
        let liveRegion = this.querySelector('.feature-tabs__live-region');
        if (!liveRegion) {
          liveRegion = document.createElement('div');
          liveRegion.className = 'feature-tabs__live-region';
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          liveRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
          this.appendChild(liveRegion);
        }

        const tabNumber = this.currentTabIndex + 1;
        const totalTabs = this.columns.length;
        liveRegion.textContent = `Tab ${tabNumber} of ${totalTabs}: ${tabTitle.textContent || tabTitle.innerText}`;
      }
    }

    handleKeydown(event) {
      const { key, target, shiftKey } = event;

      // Enhanced detection for interactive elements
      const isInteractiveElement = (
        target.tagName === 'A' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        (target.tagName === 'BUTTON' && !target.classList.contains('section-content__meta-title')) ||
        target.getAttribute('role') === 'button' ||
        target.getAttribute('contenteditable') === 'true' ||
        target.closest('[contenteditable="true"]')
      ) && !target.classList.contains('feature-tabs__column');

      // Let interactive elements handle their own navigation
      if (isInteractiveElement) {
        return;
      }

      let newIndex = this.currentTabIndex;

      switch (key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          newIndex = (this.currentTabIndex + 1) % this.columns.length;
          this.isNavigatingTabs = true;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          newIndex = (this.currentTabIndex - 1 + this.columns.length) % this.columns.length;
          this.isNavigatingTabs = true;
          break;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          this.isNavigatingTabs = true;
          break;
        case 'End':
          event.preventDefault();
          newIndex = this.columns.length - 1;
          this.isNavigatingTabs = true;
          break;
        case 'PageDown':
          if (shiftKey) {
            event.preventDefault();
            newIndex = Math.min(this.currentTabIndex + 3, this.columns.length - 1);
            this.isNavigatingTabs = true;
          }
          break;
        case 'PageUp':
          if (shiftKey) {
            event.preventDefault();
            newIndex = Math.max(this.currentTabIndex - 3, 0);
            this.isNavigatingTabs = true;
          }
          break;
        case 'Tab':
          this.isNavigatingTabs = false;
          return;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const targetColumn = target.closest('.feature-tabs__column');
          if (targetColumn && this.columns.includes(targetColumn)) {
            this.activateColumn(targetColumn, true);
          } else {
            this.columns[this.currentTabIndex].focus();
          }
          this.isNavigatingTabs = false;
          return;
        }
        default:
          this.isNavigatingTabs = false;
          return;
      }

      if (newIndex !== this.currentTabIndex) {
        this.activateColumn(this.columns[newIndex], true);
      }

      // Reset navigation flag after a delay
      setTimeout(() => {
        this.isNavigatingTabs = false;
      }, 100);
    }

    initializeTabs() {
      this.columns = this.querySelectorAll('.feature-tabs__column');
      this.contents = this.querySelectorAll('.js-section--animation');
      this.isMobile = window.matchMedia("only screen and (max-width: 767px)").matches;

      const eventType = this.isMobile ? 'click' : 'mouseenter';

      // Set up tablist role on container
      this.setAttribute('role', 'tablist');
      this.setAttribute('aria-orientation', 'horizontal');

      this.columns.forEach((column, index) => {
        // Enhanced ARIA attributes
        column.setAttribute('role', 'tab');
        column.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        column.setAttribute('tabindex', index === 0 ? '0' : '-1');
        column.setAttribute('aria-setsize', this.columns.length);
        column.setAttribute('aria-posinset', index + 1);

        // Add unique IDs for better accessibility
        const tabId = `tab-${this.id || 'tabs'}-${index}`;
        const panelId = `panel-${this.id || 'tabs'}-${index}`;

        column.setAttribute('id', tabId);
        column.setAttribute('aria-controls', panelId);

        // Set up corresponding panel
        const panel = this.contents[index];
        if (panel) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('id', panelId);
          panel.setAttribute('aria-labelledby', tabId);
          panel.setAttribute('tabindex', '0');
        }

        // Find the button inside this column
        const tabButton = column.querySelector('.section-content__meta-title');

        // Mouse/touch events
        column.addEventListener(eventType, () => {
          this.activateColumn(column);
        });

        // Enhanced click handling
        column.addEventListener('click', (e) => {
          // More comprehensive check for interactive elements
          const isInteractive = e.target.tagName === 'A' ||
            e.target.tagName === 'BUTTON' ||
            e.target.tagName === 'INPUT' ||
            e.target.getAttribute('role') === 'button' ||
            e.target.closest('a') ||
            e.target.closest('button') ||
            e.target.closest('input');

          if (isInteractive) {
            return;
          }

          e.preventDefault();
          this.activateColumn(column);
        });

        // Keyboard events on the column
        column.addEventListener('keydown', this.handleKeydown);

        // Enhanced focus management
        column.addEventListener('focus', () => {
          this.currentTabIndex = index;
        });

        // Handle button events if button exists
        if (tabButton) {
          tabButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.activateColumn(column);
            column.focus();
          });

          tabButton.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              this.activateColumn(column);
              column.focus();
            }
          });
        }
      });

      // Set initial active tab with proper setup
      if (this.columns.length > 0) {
        this.currentTabIndex = 0;
        this.columns[0].classList.add('active');
        this.columns[0].setAttribute('aria-selected', 'true');
        this.columns[0].setAttribute('tabindex', '0');

        // Make sure other tabs are not focusable
        // eslint-disable-next-line
        for (let i = 1; i < this.columns.length; i++) {
          this.columns[i].classList.remove('active');
          this.columns[i].setAttribute('aria-selected', 'false');
          this.columns[i].setAttribute('tabindex', '-1');
        }
      }
    }
  }

  const init = () => {
    if (!customElements.get("feature-tabs")) {
      customElements.define("feature-tabs", featureTabs);
    }
  };

  init();
}
